import { useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react';
import { ContactRoom } from './components/ContactRoom/ContactRoom';
import { IntroRoom } from './components/IntroRoom/IntroRoom';
import { OpenSourceRoom } from './components/OpenSourceRoom/OpenSourceRoom';
import { ProjectsRoom } from './components/ProjectsRoom/ProjectsRoom';
import { SkillsRoom } from './components/SkillsRoom/SkillsRoom';
import styles from './App.module.css';

const roomLabels = ['BOOT DESK', 'SKILL ARCADE', 'PROJECT TV', 'OPEN SOURCE', 'CONTACT'] as const;

export default function App() {
  const storyRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const roomOneRef = useRef<HTMLElement>(null);
  const roomTwoRef = useRef<HTMLElement>(null);
  const roomThreeRef = useRef<HTMLElement>(null);
  const roomFourRef = useRef<HTMLElement>(null);
  const roomFiveRef = useRef<HTMLElement>(null);
  const frameRef = useRef<number | null>(null);
  const snapTimerRef = useRef<number | null>(null);
  const idleHintTimerRef = useRef<number | null>(null);
  const swipeStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const [progress, setProgress] = useState(0);
  const [idleHintVisible, setIdleHintVisible] = useState(false);
  const roomNumber = Math.min(5, Math.round(progress * 4) + 1);

  useEffect(() => {
    const getStoryProgress = () => {
      const story = storyRef.current;
      if (!story) return null;
      const rect = story.getBoundingClientRect();
      const range = Math.max(1, story.offsetHeight - window.innerHeight);
      return Math.min(1, Math.max(0, -rect.top / range));
    };

    const update = () => {
      frameRef.current = null;
      const story = storyRef.current;
      const world = worldRef.current;
      if (!story || !world) return;
      const next = getStoryProgress();
      if (next === null) return;
      const cameraEnd = Math.max(0, world.scrollWidth - window.innerWidth);
      world.style.transform = `translate3d(${-cameraEnd * next}px,0,0)`;
      setProgress((current) => (Math.abs(current - next) > 0.002 ? next : current));
    };

    const requestUpdate = () => {
      if (frameRef.current === null) frameRef.current = window.requestAnimationFrame(update);
    };

    const snapToClosestRoom = () => {
      snapTimerRef.current = null;
      const story = storyRef.current;
      const current = getStoryProgress();
      if (!story || current === null) return;
      const target = Math.round(current * 4) / 4;
      if (Math.abs(target - current) < 0.002) return;
      const storyTop = window.scrollY + story.getBoundingClientRect().top;
      const range = Math.max(1, story.offsetHeight - window.innerHeight);
      window.scrollTo({
        top: storyTop + range * target,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };

    const handleScroll = () => {
      requestUpdate();
      if (snapTimerRef.current !== null) window.clearTimeout(snapTimerRef.current);
      snapTimerRef.current = window.setTimeout(snapToClosestRoom, 180);
    };

    update();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', requestUpdate);
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
      if (snapTimerRef.current !== null) window.clearTimeout(snapTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const scheduleIdleHint = () => {
      if (idleHintTimerRef.current !== null) window.clearTimeout(idleHintTimerRef.current);
      setIdleHintVisible(false);
      if (roomNumber > 3) return;
      idleHintTimerRef.current = window.setTimeout(() => {
        setIdleHintVisible(true);
        idleHintTimerRef.current = null;
      }, 9000);
    };

    scheduleIdleHint();
    window.addEventListener('scroll', scheduleIdleHint, { passive: true });
    window.addEventListener('pointerdown', scheduleIdleHint, { passive: true });
    window.addEventListener('keydown', scheduleIdleHint);
    return () => {
      window.removeEventListener('scroll', scheduleIdleHint);
      window.removeEventListener('pointerdown', scheduleIdleHint);
      window.removeEventListener('keydown', scheduleIdleHint);
      if (idleHintTimerRef.current !== null) window.clearTimeout(idleHintTimerRef.current);
    };
  }, [roomNumber]);

  const scrollToRoom = (roomIndex: number) => {
    const story = storyRef.current;
    if (!story) return;
    const storyTop = window.scrollY + story.getBoundingClientRect().top;
    const scrollRange = Math.max(1, story.offsetHeight - window.innerHeight);
    window.scrollTo({
      top: storyTop + scrollRange * (roomIndex / 4),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  useEffect(() => {
    const handleRoomKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        !window.matchMedia('(min-width: 681px)').matches
      ) {
        return;
      }

      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.closest('input, textarea, select, [role="textbox"]'))
      ) {
        return;
      }

      const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (direction === 0) return;

      const currentRoomIndex = roomNumber - 1;
      const nextRoomIndex = Math.min(4, Math.max(0, currentRoomIndex + direction));
      if (nextRoomIndex === currentRoomIndex) return;

      event.preventDefault();
      scrollToRoom(nextRoomIndex);
    };

    window.addEventListener('keydown', handleRoomKeyDown);
    return () => window.removeEventListener('keydown', handleRoomKeyDown);
  }, [roomNumber]);

  const handleSwipeStart = (event: ReactTouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    if (!touch) return;
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY, time: event.timeStamp };
  };

  const handleSwipeEnd = (event: ReactTouchEvent<HTMLDivElement>) => {
    const start = swipeStartRef.current;
    const touch = event.changedTouches[0];
    swipeStartRef.current = null;
    if (!start || !touch) return;
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    const isHorizontalSwipe = Math.abs(deltaX) >= 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25;
    if (!isHorizontalSwipe || event.timeStamp - start.time > 900) return;
    const nextRoomIndex = Math.min(4, Math.max(0, roomNumber - 1 + (deltaX < 0 ? 1 : -1)));
    if (nextRoomIndex !== roomNumber - 1) scrollToRoom(nextRoomIndex);
  };

  return (
    <main className={styles.game}>
      <div className={styles.scanlines} aria-hidden="true" />
      <div ref={storyRef} className={styles.story}>
        <div
          className={styles.viewport}
          onTouchStart={handleSwipeStart}
          onTouchEnd={handleSwipeEnd}
          onTouchCancel={() => {
            swipeStartRef.current = null;
          }}
        >
          <div ref={worldRef} className={styles.world}>
            <IntroRoom sectionRef={roomOneRef} showIdleHint={idleHintVisible && roomNumber === 1} />
            <SkillsRoom
              sectionRef={roomTwoRef}
              showIdleHint={idleHintVisible && roomNumber === 2}
            />
            <ProjectsRoom
              sectionRef={roomThreeRef}
              showIdleHint={idleHintVisible && roomNumber === 3}
            />
            <OpenSourceRoom sectionRef={roomFourRef} active={roomNumber === 4} />
            <ContactRoom sectionRef={roomFiveRef} active={roomNumber === 5} />
          </div>

          <nav
            className={styles.progress}
            aria-label={`Portfolio rooms. Current progress ${Math.round(progress * 100)} percent`}
          >
            <i style={{ width: `${progress * 100}%` }} />
            <strong className={styles.progressRoomTitle}>
              ROOM 0{roomNumber} / 05 · {roomLabels[roomNumber - 1]}
            </strong>
            {roomLabels.map((label, index) => (
              <button
                key={label}
                type="button"
                className={roomNumber === index + 1 ? styles.progressActive : ''}
                onClick={() => scrollToRoom(index)}
                aria-current={roomNumber === index + 1 ? 'page' : undefined}
              >
                <span>0{index + 1}</span>
                {label}
              </button>
            ))}
            <b>{Math.round(progress * 100)}%</b>
          </nav>
          <p className={styles.scrollHint}>
            <span className={styles.desktopInputHint}>
              SCROLL <b>↓</b> / ROOMS <b>← →</b>
            </span>
            <span className={styles.mobileInputHint}>
              SWIPE <b>← →</b> / SCROLL <b>↓</b>
            </span>
          </p>
        </div>
      </div>
    </main>
  );
}
